import React, { useMemo, useRef, useState } from 'react';
import {
  IonButton,
  IonContent,
  IonHeader,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonModal,
  IonPage,
  IonRefresher,
  IonRefresherContent,
  IonSegment,
  IonSegmentButton,
  IonSearchbar,
  IonSelect,
  IonSelectOption,
  IonSpinner,
  IonText,
  IonTitle,
  IonToolbar,
  useIonToast,
} from '@ionic/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CreateMenuItemDto, IMenuItem, IProduct, MenuItemStatus } from 'shared';
import apiClient from '../api/client';
import { useLanguage } from '../context/LanguageContext';
import LanguageToggle from '../components/LanguageToggle';
import UserMenu from '../components/UserMenu';
import { formatCurrency } from '../utils/format';
import { RefresherEvent } from '../types/ionic';

interface MenuFormState {
  id?: string;
  productId: string;
  price: string;
  status: MenuItemStatus;
}

const defaultMenuForm: MenuFormState = {
  productId: '',
  price: '',
  status: MenuItemStatus.ACTIVE,
};

const MenuManagement: React.FC = () => {
  const { t, language } = useLanguage();
  const locale = language === 'vi' ? 'vi-VN' : 'en-US';
  const [presentToast] = useIonToast();
  const queryClient = useQueryClient();

  const [showFormModal, setShowFormModal] = useState(false);
  const [formState, setFormState] = useState<MenuFormState>(defaultMenuForm);
  const [mode, setMode] = useState<'create' | 'edit'>('create');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const menuQuery = useQuery({
    queryKey: ['menu-items'],
    queryFn: async () => {
      const { data } = await apiClient.get<IMenuItem[]>('/menu');
      return data;
    },
  });

  const productQuery = useQuery({
    queryKey: ['products'],
    queryFn: async () => {
      const { data } = await apiClient.get<IProduct[]>('/products');
      return Array.isArray(data) ? data : [];
    },
  });

  const createMenu = useMutation({
    mutationFn: async (payload: CreateMenuItemDto) => {
      await apiClient.post('/menu', payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['menu-items'] });
      presentToast({ message: t('menuItemCreated') ?? 'Menu item created', duration: 2000, color: 'success' });
      closeForm();
    },
    onError: () => {
      presentToast({ message: t('menuItemCreateFailed') ?? 'Failed to create menu item', duration: 2000, color: 'danger' });
    },
  });

  const updateMenu = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<CreateMenuItemDto> }) => {
      await apiClient.put(`/menu/${id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['menu-items'] });
      presentToast({ message: t('menuItemUpdated') ?? 'Menu item updated', duration: 2000, color: 'success' });
      closeForm();
    },
    onError: () => {
      presentToast({ message: t('menuItemUpdateFailed') ?? 'Failed to update menu item', duration: 2000, color: 'danger' });
    },
  });

  const deleteMenu = useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/menu/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['menu-items'] });
      presentToast({ message: t('menuItemDeleted') ?? 'Menu item deleted', duration: 2000, color: 'success' });
    },
    onError: () => {
      presentToast({ message: t('menuItemDeleteFailed') ?? 'Failed to delete menu item', duration: 2000, color: 'danger' });
    },
  });

  const filteredMenuItems = useMemo(() => {
    const data = menuQuery.data ?? [];
    return data
      .filter((item) => {
        if (statusFilter === 'active') return item.status === MenuItemStatus.ACTIVE;
        if (statusFilter === 'inactive') return item.status === MenuItemStatus.INACTIVE;
        return true;
      })
      .filter((item) => {
        const productName = item.product?.name?.toLowerCase() ?? '';
        return productName.includes(searchTerm.trim().toLowerCase());
      });
  }, [menuQuery.data, statusFilter, searchTerm]);

  const closeForm = () => {
    if (createMenu.isPending || updateMenu.isPending) return;
    setShowFormModal(false);
    setFormState(defaultMenuForm);
  };

  const openCreateModal = () => {
    setMode('create');
    setFormState(defaultMenuForm);
    setShowFormModal(true);
  };

  const openEditModal = (item: IMenuItem) => {
    setMode('edit');
    setFormState({
      id: item.id,
      productId: item.productId,
      price: String(item.price ?? ''),
      status: item.status ?? MenuItemStatus.ACTIVE,
    });
    setShowFormModal(true);
  };

  const submitForm = () => {
    if (!formState.productId) {
      presentToast({ message: t('selectProduct') ?? 'Select product', duration: 2000, color: 'warning' });
      return;
    }
    const price = Number(formState.price);
    if (Number.isNaN(price) || price <= 0) {
      presentToast({ message: t('enterValidPrice') ?? 'Enter a valid price', duration: 2000, color: 'warning' });
      return;
    }
    const payload: CreateMenuItemDto = {
      productId: formState.productId,
      price,
      status: formState.status,
    };
    if (mode === 'create') {
      createMenu.mutate(payload);
    } else if (formState.id) {
      updateMenu.mutate({ id: formState.id, data: payload });
    }
  };

  const handleRefresh = async (event: RefresherEvent) => {
    try {
      await menuQuery.refetch();
      await productQuery.refetch();
    } finally {
      event.detail.complete();
    }
  };

  const handleExport = () => {
    const data = menuQuery.data ?? [];
    if (data.length === 0) {
      presentToast({ message: t('noData') ?? 'No data available', duration: 2000, color: 'warning' });
      return;
    }
    const header = ['id', 'productName', 'price', 'status'];
    const rows = data.map((item) => [
      item.id,
      item.product?.name ?? '',
      item.price,
      item.status,
    ]);
    const csv = [header, ...rows]
      .map((row) => row.map((value) => `"${String(value ?? '').replace(/"/g, '""')}"`).join(','))
      .join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `menu-items-${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    presentToast({ message: t('menuExported') ?? 'Menu exported', duration: 2000, color: 'success' });
  };

  const handleImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const [headerLine, ...lines] = text.split(/\r?\n/).filter(Boolean);
      const headers = headerLine.split(',').map((h) => h.replace(/"/g, '').trim().toLowerCase());
      const idIndex = headers.indexOf('id');
      const priceIndex = headers.indexOf('price');
      const statusIndex = headers.indexOf('status');
      if (idIndex === -1 || priceIndex === -1) {
        throw new Error('Invalid CSV format');
      }
      await Promise.all(
        lines.map(async (line) => {
          const columns = line.split(',').map((c) => c.replace(/"/g, '').trim());
          const id = columns[idIndex];
          const price = Number(columns[priceIndex]);
          const statusValue = statusIndex !== -1 ? (columns[statusIndex] as MenuItemStatus) : undefined;
          if (!id || Number.isNaN(price)) return;
          const payload: Partial<CreateMenuItemDto> = { price };
          if (statusValue === MenuItemStatus.ACTIVE || statusValue === MenuItemStatus.INACTIVE) {
            payload.status = statusValue;
          }
          await apiClient.put(`/menu/${id}`, payload);
        }),
      );
      queryClient.invalidateQueries({ queryKey: ['menu-items'] });
      presentToast({ message: t('menuImported') ?? 'Menu imported', duration: 2000, color: 'success' });
    } catch (error) {
      presentToast({ message: t('menuImportFailed') ?? 'Failed to import menu', duration: 2000, color: 'danger' });
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <IonPage>
      <IonHeader translucent>
        <IonToolbar>
          <IonTitle>{t('menuManagement') ?? 'Menu management'}</IonTitle>
          <LanguageToggle />
          <UserMenu />
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen className="ion-padding">
        <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
          <IonRefresherContent pullingText="Pull to refresh" />
        </IonRefresher>

        <div style={{ paddingTop: 'var(--ion-safe-area-top)' }}>
          <section style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
            <IonButton onClick={openCreateModal}>{t('addMenuItem') ?? 'Add menu item'}</IonButton>
            <IonButton color="secondary" onClick={handleExport}>
              {t('exportCsv') ?? 'Export CSV'}
            </IonButton>
            <IonButton
              color="tertiary"
              onClick={() => fileInputRef.current?.click()}
            >
              {t('importCsv') ?? 'Import CSV'}
            </IonButton>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              style={{ display: 'none' }}
              onChange={handleImport}
            />
          </section>

          <IonSearchbar
            value={searchTerm}
            onIonChange={(event) => setSearchTerm(event.detail.value ?? '')}
            placeholder={t('searchProducts') ?? 'Search products'}
          />
          <IonSegment
            value={statusFilter}
            onIonChange={(event) => {
              const value = (event.detail.value as 'all' | 'active' | 'inactive' | undefined) ?? 'all';
              setStatusFilter(value);
            }}
          >
            <IonSegmentButton value="all">
              <IonLabel>{t('all')}</IonLabel>
            </IonSegmentButton>
            <IonSegmentButton value="active">
              <IonLabel>{t('active')}</IonLabel>
            </IonSegmentButton>
            <IonSegmentButton value="inactive">
              <IonLabel>{t('inactive')}</IonLabel>
            </IonSegmentButton>
          </IonSegment>

          {menuQuery.isLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '48px 0' }}>
              <IonSpinner />
            </div>
          ) : filteredMenuItems.length === 0 ? (
            <IonText color="medium">
              <p>{t('noData')}</p>
            </IonText>
          ) : (
            <IonList>
              {filteredMenuItems.map((item) => (
                <IonItem key={item.id} button detail onClick={() => openEditModal(item)}>
                  <IonLabel>
                    <h2 style={{ fontWeight: 600 }}>{item.product?.name || t('unknown')}</h2>
                    <p>
                      {t('price')}: {formatCurrency(item.price ?? 0, locale)} • {t('status')}:{' '}
                      {item.status === MenuItemStatus.ACTIVE ? t('active') : t('inactive')}
                    </p>
                  </IonLabel>
                  <IonButton
                    slot="end"
                    color="danger"
                    fill="clear"
                    onClick={(event) => {
                      event.stopPropagation();
                      deleteMenu.mutate(item.id);
                    }}
                  >
                    {t('delete')}
                  </IonButton>
                </IonItem>
              ))}
            </IonList>
          )}
        </div>

        <IonModal isOpen={showFormModal} onDidDismiss={closeForm}>
          <IonHeader>
            <IonToolbar>
              <IonTitle>{mode === 'create' ? t('addMenuItem') ?? 'Add menu item' : t('editMenuItem') ?? 'Edit menu item'}</IonTitle>
              <IonButton slot="end" fill="clear" onClick={closeForm}>
                {t('close')}
              </IonButton>
            </IonToolbar>
          </IonHeader>
          <IonContent className="ion-padding">
            <IonList>
              <IonItem>
                <IonLabel position="stacked">{t('product')}</IonLabel>
                <IonSelect
                  value={formState.productId}
                  onIonChange={(event) => setFormState((prev) => ({ ...prev, productId: event.detail.value ?? '' }))}
                >
                  {(productQuery.data ?? []).map((product) => (
                    <IonSelectOption key={product.id} value={product.id}>
                      {product.name}
                    </IonSelectOption>
                  ))}
                </IonSelect>
              </IonItem>
              <IonItem>
                <IonLabel position="stacked">{t('price')}</IonLabel>
                <IonInput
                  type="number"
                  value={formState.price}
                  onIonChange={(event) => setFormState((prev) => ({ ...prev, price: event.detail.value ?? '' }))}
                />
              </IonItem>
              <IonItem>
                <IonLabel position="stacked">{t('status')}</IonLabel>
                <IonSelect
                  value={formState.status}
                  onIonChange={(event) =>
                    setFormState((prev) => ({ ...prev, status: (event.detail.value as MenuItemStatus) ?? MenuItemStatus.ACTIVE }))
                  }
                >
                  <IonSelectOption value={MenuItemStatus.ACTIVE}>{t('active')}</IonSelectOption>
                  <IonSelectOption value={MenuItemStatus.INACTIVE}>{t('inactive')}</IonSelectOption>
                </IonSelect>
              </IonItem>
            </IonList>
            <IonButton
              expand="block"
              style={{ marginTop: 16 }}
              onClick={submitForm}
              disabled={createMenu.isPending || updateMenu.isPending}
            >
              {mode === 'create' ? t('save') : t('update')}
            </IonButton>
          </IonContent>
        </IonModal>
      </IonContent>
    </IonPage>
  );
};

export default MenuManagement;
